import {
  DEFAULT_EDGE_PRODUCTS,
  defaultEdgeRemovalPartNoFromTargetRef,
  shouldInjectDefaultEdgeRows,
  solutionHasExplicitEdgeOutput,
  type DefaultEdgeProductPartNo,
  type SourceRef
} from "@automatry/schemas";

import { isCablingDescriptor } from "./spec/compareVM";

const POINT_KEYS = ["ui", "di", "ao", "do", "relay"] as const;
type PointKey = (typeof POINT_KEYS)[number];
type RiskLevel = "none" | "info" | "warning" | "blocker";
type DisplaySupplySource = "Titan" | "Fabricated" | "3rd-party" | "In-house";
type CatalogSupplySource = "titan_supplied" | "fabricated" | "in_house" | "third_party";
type ValidationLevel = "accepted" | "review" | "error" | "info";
type ViewStatus = "loading" | "ready" | "blocked" | "error";
type MatrixAnomalyMetric = "controllers" | "zones" | "uiScreens" | "ioModules";
export type WorkspaceSectionId =
  | "controllers"
  | "room-ui"
  | "sensors"
  | "relays-panel"
  | "edge-leak"
  | "services"
  | "other";

export interface PointVector {
  ui: number;
  di: number;
  ao: number;
  do: number;
  relay: number;
}

interface SpecRiskDisposition {
  level: RiskLevel;
  riskRefs: string[];
  notes: string;
}

interface SpecContext {
  why: string;
  how: string;
  sourceRefs: SourceRef[];
  ruleTraceRefs: string[];
  risk: SpecRiskDisposition;
}

interface PointViolation {
  pointKind: PointKey;
  message: string;
}

interface PointLedger extends SpecContext {
  required: PointVector;
  available: PointVector;
  reserved: PointVector;
  spare: PointVector;
  violations: PointViolation[];
}

interface ControllerAssignment extends SpecContext {
  id: string;
  productPartNo: string;
  role: string;
  physicalLocation: string;
  zoneSlots: readonly unknown[];
  pointLedger: PointLedger;
  status: "draft" | "accepted" | "needs_review";
}

interface InfrastructureItem extends SpecContext {
  id: string;
  itemKind: string;
  supplySource: CatalogSupplySource;
  installIncluded: boolean;
  installIncludedBasis?: string;
  quantity: number;
  productPartNo?: string;
  protocols?: string[];
  connectivity?: string;
  provisionScope?: "apartment_type" | "project";
  provisionScopeBasis?: string;
  selectionStatus?: string;
  capabilityRole?: string;
  placeholderName?: string;
}

interface DerivedDevice extends SpecContext {
  id: string;
  productPartNo: string;
  family: string;
  role: string;
  quantity: number;
  provisionScope?: "apartment_type" | "project";
  provisionScopeBasis?: string;
  ownerRef?: string;
  pointDemandRefs: string[];
}

interface EdgeFeature extends SpecContext {
  id: string;
  featureKind: string;
  provisionUnit: string;
  inputPolicy: string;
  connectivity?: string;
  installNotes: string[];
  needsUserDecision: boolean;
}

interface LogicalZone {
  zoneKind: string;
}

interface ThermalLoad {
  loadKind: string;
  quantity: number;
}

interface PointDemandForTakeoff {
  demandKind?: string;
  points?: Partial<PointVector>;
  quantity?: number;
}

interface Risk {
  id: string;
  projectId?: string;
  type: string;
  severity?: "info" | "warning" | "blocker";
  scope?: {
    level: string;
    refId?: string;
    label?: string;
  };
  title: string;
  plainEnglish: string;
  technicalTrace?: readonly unknown[];
  recommendedActions?: readonly {
    action: string;
    owner: string;
    status: string;
  }[];
  status: string;
  blocksExport: boolean;
  sourceRefs?: readonly unknown[];
  ruleTraceRefs?: readonly string[];
  createdAt?: string;
}

interface Justification {
  id: string;
  targetRef?: string;
  assumptions: string[];
  sourceRefs?: readonly unknown[];
  ruleTraceRefs?: readonly string[];
  createdAt?: string;
}

interface ValidationResult {
  status: "pending" | "accepted" | "accepted_with_risks" | "rejected";
}

export interface ApartmentSolutionForDisplay extends SpecContext {
  apartmentTypeId: string;
  quantity: number;
  logicalZones: LogicalZone[];
  thermalLoads: ThermalLoad[];
  controllers: ControllerAssignment[];
  devices: DerivedDevice[];
  edgeFeatures?: EdgeFeature[];
  infrastructure: InfrastructureItem[];
  physicalMappings: { id: string }[];
  mappings: { id: string }[];
  pointLedger: PointLedger;
  risks: Risk[];
  justifications: Justification[];
  validation: ValidationResult;
}

export interface DisplayRiskBadge {
  level: RiskLevel;
  label: string;
  riskRefs: string[];
  notes: string;
}

export interface DisplayValidationBadge {
  level: ValidationLevel;
  label: string;
}

export interface WorkspacePointUse {
  kind: string;
  required: number;
  available: number;
  spare: number;
  reserved: number;
  violations: string[];
}

export interface WorkspaceTakeoffStat {
  key: string;
  label: string;
  value: number;
  tone: "controller" | "heating" | "loads" | "network" | "risk" | "neutral";
}

export interface WorkspaceItemInstanceDetail {
  id: string;
  label: string;
  role: string;
  quantity: number;
  unit: string;
  why: string;
  how: string;
  detailLines: string[];
  sourceItemIds: string[];
  sourceRefCount: number;
  ruleTraceRefCount: number;
}

export interface WorkspaceItem {
  id: string;
  category: string;
  sectionId: WorkspaceSectionId;
  name: string;
  partNo?: string;
  role: string;
  physicalLocation?: string;
  targetCollection?: "controllers" | "devices" | "infrastructure";
  /** Whether the source row is allocated per apartment type or once project-wide. */
  provisionScope?: "apartment_type" | "project";
  provisionScopeBasis?: string;
  selectorRole?: string;
  selectorItemKind?: string;
  quantity: number;
  unit: string;
  why: string;
  how: string;
  source: DisplaySupplySource;
  supplySource?: CatalogSupplySource;
  riskBadge: DisplayRiskBadge;
  validationBadge: DisplayValidationBadge;
  points: WorkspacePointUse[];
  detailLines: string[];
  sourceRefCount: number;
  sourceRefs: SourceRef[];
  ruleTraceRefs: string[];
  sourceItemIds: string[];
  instanceCount: number;
  isGrouped: boolean;
  overCapacity?: boolean;
  subItems: WorkspaceItemInstanceDetail[];
}

export interface WorkspaceSection {
  id: WorkspaceSectionId;
  title: string;
  description: string;
  itemIds: string[];
  totalQuantity: number;
}

export interface WorkspaceSummaryFact {
  id: string;
  label: string;
  value: number;
  detail?: string;
  tone: WorkspaceTakeoffStat["tone"];
}

export interface WorkspaceSummary {
  sentence: string;
  facts: WorkspaceSummaryFact[];
  groupedLineCount: number;
  physicalItemCount: number;
  sourceRowCount: number;
}

export interface WorkspaceNote {
  id: string;
  kind:
    | "clarification"
    | "exclusion"
    | "commissioning"
    | "requirement-amendment"
    | "risk"
    | "assumption";
  title: string;
  body: string;
  noPriceImpact: boolean;
  linkedItemIds: string[];
}

export interface ApartmentSolutionDisplayViewModel {
  projectId: string;
  apartmentTypeId: string;
  specSnapshotId: string;
  status: ViewStatus;
  controllerIds: string[];
  deviceIds: string[];
  mappingIds: string[];
  riskIds: string[];
  updatedAt: string;
  title: string;
  subtitle: string;
  apartmentQuantity: number;
  beds?: number;
  takeoffs: WorkspaceTakeoffStat[];
  summary: WorkspaceSummary;
  sections: WorkspaceSection[];
  items: WorkspaceItem[];
  pointLedger: {
    required: PointVector;
    available: PointVector;
    reserved: PointVector;
    spare: PointVector;
    violations: string[];
    rows: WorkspacePointUse[];
  };
  notes: WorkspaceNote[];
  dataGaps: string[];
  generatedFrom: string;
}

interface SpecMatrixColumn {
  apartmentTypeId: string;
  label: string;
  quantity: number;
  beds?: number;
  peerGroupId: string;
  peerGroupLabel: string;
  anomalyCount: number;
  anomalies: SpecMatrixColumnAnomaly[];
}

interface SpecMatrixCell {
  apartmentTypeId: string;
  quantity: number;
  source?: DisplaySupplySource;
  riskLevel: RiskLevel;
  validationLabel: string;
  isAnomaly?: boolean;
  anomalyMetric?: MatrixAnomalyMetric;
  anomalyLabel?: string;
}

interface SpecMatrixRow {
  id: string;
  label: string;
  category: string;
  cells: SpecMatrixCell[];
}

interface SpecMatrixGroup {
  id: string;
  label: string;
  rows: SpecMatrixRow[];
}

interface SpecMatrixColumnAnomaly {
  metric: MatrixAnomalyMetric;
  label: string;
  value: number;
  expected: number;
  delta: number;
  peerGroupLabel: string;
  summary: string;
}

interface SpecMatrixColumnGroup {
  id: string;
  label: string;
  description: string;
  columnKeys: string[];
  anomalyCount: number;
}

export interface SpecMatrixDisplayViewModel {
  projectId: string;
  specSnapshotId: string;
  status: ViewStatus;
  apartmentTypeIds: string[];
  columnKeys: string[];
  updatedAt: string;
  columns: SpecMatrixColumn[];
  columnGroups: SpecMatrixColumnGroup[];
  groups: SpecMatrixGroup[];
  dataGaps: string[];
  generatedFrom: string;
}

interface ApartmentTypeDisplayMeta {
  apartmentTypeId: string;
  label?: string;
  beds?: number;
}

export interface DisplayViewOptions {
  projectId: string;
  specSnapshotId: string;
  updatedAt: string;
  generatedFrom: string;
  apartmentTypes?: readonly ApartmentTypeDisplayMeta[];
}

const zeroPointVector = (): PointVector => ({
  ui: 0,
  di: 0,
  ao: 0,
  do: 0,
  relay: 0
});

const addPointVectors = (left: PointVector, right: PointVector): PointVector => ({
  ui: left.ui + right.ui,
  di: left.di + right.di,
  ao: left.ao + right.ao,
  do: left.do + right.do,
  relay: left.relay + right.relay
});

const maxRiskLevel = (risks: readonly SpecRiskDisposition[]): SpecRiskDisposition => {
  const rank = { none: 0, info: 1, warning: 2, blocker: 3 } as const;
  const winner = risks.reduce<SpecRiskDisposition | undefined>((current, risk) => {
    if (!current || rank[risk.level] > rank[current.level]) {
      return risk;
    }

    return current;
  }, undefined) ?? {
    level: "none",
    riskRefs: [],
    notes: "No risk identified for this derived item."
  };

  const riskRefs = [...new Set(risks.flatMap((risk) => risk.riskRefs))];

  return {
    level: winner.level,
    riskRefs,
    notes:
      winner.level === "none"
        ? "No risk identified for this derived item."
        : winner.notes
  };
};

export const displaySupplySource = (
  supplySource: CatalogSupplySource | undefined
): DisplaySupplySource => {
  if (supplySource === "fabricated") {
    return "Fabricated";
  }

  if (supplySource === "third_party") {
    return "3rd-party";
  }

  if (supplySource === "in_house") {
    return "In-house";
  }

  return "Titan";
};

const riskBadge = (risk: SpecRiskDisposition): DisplayRiskBadge => {
  const labelByLevel = {
    none: "No risk",
    info: "Info",
    warning: "Review",
    blocker: "Blocker"
  } as const;

  return {
    level: risk.level,
    label: labelByLevel[risk.level],
    riskRefs: risk.riskRefs,
    notes: risk.notes
  };
};

const validationBadge = (
  risk: SpecRiskDisposition,
  status?: string,
  violationCount = 0
): DisplayValidationBadge => {
  if (risk.level === "blocker" || violationCount > 0) {
    return { level: "error", label: "Validation issue" };
  }

  if (risk.level === "warning" || status === "needs_review") {
    return { level: "review", label: "Needs review" };
  }

  if (status === "draft") {
    return { level: "info", label: "Draft" };
  }

  return { level: "accepted", label: "Accepted" };
};

const pointRows = (ledger: PointLedger): WorkspacePointUse[] =>
  POINT_KEYS.map((key) => ({
    kind: key.toUpperCase(),
    required: ledger.required[key],
    available: ledger.available[key],
    reserved: ledger.reserved[key],
    spare: ledger.spare[key],
    violations: ledger.violations
      .filter((violation) => violation.pointKind === key)
      .map((violation) => violation.message)
  }));

const pointRowsFromVectors = (vectors: {
  required: PointVector;
  available: PointVector;
  reserved: PointVector;
  spare: PointVector;
  violations: string[];
}): WorkspacePointUse[] =>
  POINT_KEYS.map((key) => ({
    kind: key.toUpperCase(),
    required: vectors.required[key],
    available: vectors.available[key],
    reserved: vectors.reserved[key],
    spare: vectors.spare[key],
    violations: vectors.violations
  }));

const sourceRefKey = (sourceRef: SourceRef): string =>
  [
    sourceRef.sourceId,
    sourceRef.locator,
    sourceRef.sheetName ?? "",
    sourceRef.cellRef ?? "",
    sourceRef.rangeRef ?? ""
  ].join("\u001f");

const uniqueSourceRefs = (sourceRefs: readonly SourceRef[]): SourceRef[] => [
  ...new Map(
    sourceRefs.map((sourceRef) => [sourceRefKey(sourceRef), sourceRef])
  ).values()
];

const cleanPartNo = (value: string | undefined): string | undefined =>
  value && value.length > 0 ? value : undefined;

const controllerGroupKey = (controller: ControllerAssignment): string =>
  `${controller.productPartNo}:${controller.role}:${controller.physicalLocation}`;

const controllerIsOverCapacity = (ledger: PointLedger): boolean =>
  POINT_KEYS.some((key) => ledger.required[key] > ledger.available[key]) ||
  ledger.violations.length > 0;

const labelise = (value: string): string =>
  value
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());

const workspaceTextReplacements: readonly [RegExp, string][] = [
  [
    /Panel trigger keys:[^.]*?Tier policy rank \d+;\s*contents:\s*\{[^}]+\}\.?/gi,
    "Panel capacity is driven by the selected hub, I/O, relay and controller scope."
  ],
  [/\bWUFH\b/gi, "wet underfloor heating"],
  [/\bfcu_primary\b/gi, "fan-coil controller"],
  [/\bheat_only_710\b/gi, "heat-only controller"],
  [/\bmz_overflow\b/gi, "overflow zone controller"],
  [/\bbase_netmaster_panel\b/gi, "base Netmaster panel"],
  [/\bexpanded_controls_panel\b/gi, "expanded controls panel"],
  [/\bcustom_central_panel\b/gi, "custom central panel"],
  [/\bmetering_netmaster_panel\b/gi, "metering Netmaster panel"],
  [/\bcontrol_panel_enclosure\b/gi, "control panel enclosure"],
  [/\bio_module\b/gi, "I/O module"],
  [/\bload_relay\b/gi, "load relay"],
  [/\bmz_controller\b/gi, "overflow controller"],
  [/\bcentral_enable\b/gi, "central panel allowance"],
  [/\bload_relay_module\b/gi, "load relay module"],
  [/\bnetwork_switch\b/gi, "network switch"],
  [/\bedge_compute\b/gi, "Edge gateway"],
  [/\bedge_enclosure\b/gi, "Edge gateway"],
  [/\bedge_network_switch\b/gi, "Edge gateway"],
  [/\bcloud_service\b/gi, "Edge gateway"]
];

const humaniseWorkspaceText = (value: string): string =>
  workspaceTextReplacements
    .reduce(
      (current, [pattern, replacement]) => current.replace(pattern, replacement),
      value
    )
    .replace(/\s+/g, " ")
    .trim();

const humaniseWorkspaceLines = (values: readonly string[]): string[] =>
  values.map(humaniseWorkspaceText);

const safeId = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 96);

const pluralise = (count: number, singular: string, plural = `${singular}s`): string =>
  `${count} ${count === 1 ? singular : plural}`;

const DEFAULT_NO_TRIGGER_LABEL =
  "Default - no specific requirement found in the documents";

const DEFAULT_EDGE_WHY = `${DEFAULT_NO_TRIGGER_LABEL}. Edge is included for every controls project unless removed during review.`;
const DEFAULT_EDGE_WHERE =
  "Applied in the workspace viewmodel; no source trail is emitted for this default row.";
const DEFAULT_EDGE_PRODUCT_DISPLAY: Record<
  DefaultEdgeProductPartNo,
  {
    sectionId: WorkspaceSectionId;
    category: WorkspaceItem["category"];
    detailLines: readonly string[];
  }
> = {
  "AUTO-EDGE-GATEWAY": {
    sectionId: "edge-leak",
    category: "Products",
    detailLines: [
      "Self-contained Edge gateway PC",
      "BACnet/IP, HTTPS, dashboard and alert capable"
    ]
  }
};

export const controllerRoleLabel = (role: string): string => {
  if (role === "fcu_primary") {
    return "Fan-coil controller";
  }

  if (role === "heat_only_710") {
    return "Heat-only controller";
  }

  if (role === "mz_overflow") {
    return "Overflow zone controller";
  }

  return labelise(role);
};

const productRoleLabel = (role: string): string => {
  const labels: Record<string, string> = {
    local_ui_ts4: "Local room screen",
    central_control_screen_ts7: "Central control screen",
    control_temperature_sensor: "Control temperature sensor",
    ufh_limit_sensor_active: "UFH limit sensor (active)",
    ufh_limit_sensor_spare: "UFH limit sensor (spare)",
    supply_air_off_coil_sensor: "Supply-air sensor",
    load_relay_module: "Relay output module",
    relay: "Load relay",
    io_module: "I/O module",
    network_switch: "Network switch",
    control_panel_enclosure: "Control panel enclosure",
    edge_gateway: "Edge gateway",
    edge_compute: "Edge gateway",
    edge_network_switch: "Edge gateway",
    edge_enclosure: "Edge gateway",
    cloud_service: "Edge gateway",
    netmaster: "Netmaster"
  };

  return labels[role] ?? labelise(role);
};

const unitForQuantity = (quantity: number, category: string): string => {
  if (category === "Controllers") {
    return quantity === 1 ? "unit" : "units";
  }

  if (category === "Extensions") {
    return quantity === 1 ? "feature" : "features";
  }

  return quantity === 1 ? "item" : "items";
};

const workspaceSectionDefinitions: Record<
  WorkspaceSectionId,
  { title: string; description: string }
> = {
  controllers: {
    title: "Controllers",
    description: "Primary controller hardware allocated to run the apartment plant."
  },
  "room-ui": {
    title: "Room screens / UI",
    description: "Screens a resident or engineer actually sees and uses."
  },
  sensors: {
    title: "Sensors",
    description:
      "Field sensors used for room control, UFH limiting and FCU supply-air checks."
  },
  "relays-panel": {
    title: "Relays & panel",
    description:
      "Panel-mounted support hardware: Netmaster, relay outputs, relays and enclosure."
  },
  "edge-leak": {
    title: "Edge / leak detection",
    description:
      "Apartment Edge hardware and generated leak-detection feature allowances."
  },
  services: {
    title: "Services",
    description: "Apartment Edge gateway and leak-detection workflow items."
  },
  other: {
    title: "Other generated items",
    description: "Generated rows that do not fit the standard product headings."
  }
};

const workspaceSectionOrder: WorkspaceSectionId[] = [
  "controllers",
  "room-ui",
  "sensors",
  "relays-panel",
  "edge-leak",
  "services",
  "other"
];

const sectionForItem = (item: WorkspaceItem): WorkspaceSectionId => {
  if (item.category === "Controllers") {
    return "controllers";
  }

  const text = `${item.name} ${item.partNo ?? ""} ${item.role} ${
    item.selectorItemKind ?? ""
  }`.toLowerCase();

  if (/\bcloud\b|service|subscription|sms/.test(text)) {
    return "services";
  }

  if (/edge|leak/.test(text)) {
    return "edge-leak";
  }

  if (/screen|ui|display|ts-4|ts-7|rdu/.test(text)) {
    return "room-ui";
  }

  if (/sensor|temperature|tpfmts|tptvav/.test(text)) {
    return "sensors";
  }

  if (/relay|panel|netmaster|io\/com|io module|enclosure/.test(text)) {
    return "relays-panel";
  }

  return "other";
};

const aggregateRoleForItem = (
  item: WorkspaceItem
): { key: string; label: string; mergeVariantRationale: boolean } => {
  const roleKey = item.selectorRole ?? item.role;

  if (
    item.partNo === "TPTVAV/L" &&
    roleKey.toLowerCase().includes("ufh_limit_sensor")
  ) {
    return {
      key: "ufh_limit_sensor",
      label: "UFH limit sensor",
      mergeVariantRationale: true
    };
  }

  return {
    key: roleKey,
    label: item.role,
    mergeVariantRationale: false
  };
};

const workspaceItemGroupKey = (item: WorkspaceItem): string => {
  const role = aggregateRoleForItem(item);

  return [
    item.targetCollection ?? item.category,
    item.provisionScope ?? "",
    item.partNo ?? item.name,
    role.key,
    item.source,
    item.supplySource ?? "",
    item.why,
    role.mergeVariantRationale ? "variant-split" : item.how
  ].join("\u001f");
};

const maxDisplayRiskBadge = (badges: readonly DisplayRiskBadge[]): DisplayRiskBadge => {
  const rank = { none: 0, info: 1, warning: 2, blocker: 3 } as const;
  const winner = badges.reduce<DisplayRiskBadge | undefined>((current, badge) => {
    if (!current || rank[badge.level] > rank[current.level]) {
      return badge;
    }

    return current;
  }, undefined) ?? {
    level: "none",
    label: "No risk",
    riskRefs: [],
    notes: "No risk identified for this derived item."
  };

  return {
    ...winner,
    riskRefs: [...new Set(badges.flatMap((badge) => badge.riskRefs))]
  };
};

const maxDisplayValidationBadge = (
  badges: readonly DisplayValidationBadge[]
): DisplayValidationBadge => {
  const rank = { accepted: 0, info: 1, review: 2, error: 3 } as const;

  return (
    badges.reduce<DisplayValidationBadge | undefined>((current, badge) => {
      if (!current || rank[badge.level] > rank[current.level]) {
        return badge;
      }

      return current;
    }, undefined) ?? { level: "accepted", label: "Accepted" }
  );
};

const mergePointUses = (items: readonly WorkspaceItem[]): WorkspacePointUse[] => {
  if (!items.some((item) => item.points.length > 0)) {
    return [];
  }

  return POINT_KEYS.map((key) => {
    const kind = key.toUpperCase();
    const rows = items.flatMap((item) =>
      item.points.filter((point) => point.kind.toLowerCase() === key)
    );

    return {
      kind,
      required: rows.reduce((sum, row) => sum + row.required, 0),
      available: rows.reduce((sum, row) => sum + row.available, 0),
      reserved: rows.reduce((sum, row) => sum + row.reserved, 0),
      spare: rows.reduce((sum, row) => sum + row.spare, 0),
      violations: rows.flatMap((row) => row.violations)
    };
  });
};

const uniqueStrings = (values: readonly string[]): string[] => [
  ...new Set(values.filter((value) => value.length > 0))
];

const commonValue = <T>(values: readonly (T | undefined)[]): T | undefined => {
  const defined = values.filter((value): value is T => value !== undefined);
  const first = defined[0];

  if (first === undefined) {
    return undefined;
  }

  return defined.every((value) => value === first) ? first : undefined;
};

const rawSubItem = (item: WorkspaceItem): WorkspaceItemInstanceDetail => ({
  id: `${item.id}-detail`,
  label: item.role,
  role: item.role,
  quantity: item.quantity,
  unit: item.unit,
  why: humaniseWorkspaceText(item.why),
  how: humaniseWorkspaceText(item.how),
  detailLines: humaniseWorkspaceLines(item.detailLines),
  sourceItemIds: item.sourceItemIds,
  sourceRefCount: item.sourceRefCount,
  ruleTraceRefCount: item.ruleTraceRefs.length
});

const mergeSubItems = (
  items: readonly WorkspaceItem[]
): WorkspaceItemInstanceDetail[] => {
  const groups = new Map<string, WorkspaceItemInstanceDetail[]>();

  for (const item of items) {
    const subItem = item.subItems[0] ?? rawSubItem(item);
    const key = [subItem.role, subItem.why, subItem.how].join("\u001e");

    groups.set(key, [...(groups.get(key) ?? []), subItem]);
  }

  return [...groups.values()].map((group) => {
    const first = group[0];

    if (!first) {
      throw new Error("Workspace item sub-group has no rows.");
    }

    const quantity = group.reduce((sum, item) => sum + item.quantity, 0);

    return {
      ...first,
      id: `${first.id}-x${quantity}`,
      quantity,
      unit:
        quantity === first.quantity || first.unit.endsWith("s")
          ? first.unit
          : `${first.unit}s`,
      detailLines: uniqueStrings(group.flatMap((item) => item.detailLines)),
      sourceItemIds: group.flatMap((item) => item.sourceItemIds),
      sourceRefCount: group.reduce((sum, item) => sum + item.sourceRefCount, 0),
      ruleTraceRefCount: group.reduce((sum, item) => sum + item.ruleTraceRefCount, 0)
    };
  });
};

const controllerItems = (solution: ApartmentSolutionForDisplay): WorkspaceItem[] => {
  const groups = new Map<string, ControllerAssignment[]>();

  for (const controller of solution.controllers) {
    const key = controllerGroupKey(controller);
    groups.set(key, [...(groups.get(key) ?? []), controller]);
  }

  return [...groups.entries()].map(([key, controllers]) => {
    const first = controllers[0];

    if (!first) {
      throw new Error(`Controller group ${key} has no controllers.`);
    }

    const required = controllers.reduce(
      (sum, controller) => addPointVectors(sum, controller.pointLedger.required),
      zeroPointVector()
    );
    const available = controllers.reduce(
      (sum, controller) => addPointVectors(sum, controller.pointLedger.available),
      zeroPointVector()
    );
    const reserved = controllers.reduce(
      (sum, controller) => addPointVectors(sum, controller.pointLedger.reserved),
      zeroPointVector()
    );
    const spare = controllers.reduce(
      (sum, controller) => addPointVectors(sum, controller.pointLedger.spare),
      zeroPointVector()
    );
    const violations = controllers.flatMap((controller) =>
      controller.pointLedger.violations.map((violation) => violation.message)
    );
    const risk = maxRiskLevel(controllers.map((controller) => controller.risk));
    const sourceRefs = uniqueSourceRefs(
      controllers.flatMap((controller) => controller.sourceRefs)
    );

    const item: WorkspaceItem = {
      id: `workspace-controller-${first.productPartNo}-${first.role}`,
      category: "Controllers",
      sectionId: "controllers",
      name: first.productPartNo,
      ...(cleanPartNo(first.productPartNo) ? { partNo: first.productPartNo } : {}),
      role: controllerRoleLabel(first.role),
      targetCollection: "controllers",
      selectorRole: first.role,
      quantity: controllers.length,
      unit: controllers.length === 1 ? "unit" : "units",
      why: humaniseWorkspaceText(first.why),
      how: humaniseWorkspaceText(first.how),
      source: "Titan",
      supplySource: "titan_supplied",
      riskBadge: riskBadge(risk),
      validationBadge: validationBadge(
        risk,
        controllers.some((controller) => controller.status === "needs_review")
          ? "needs_review"
          : "accepted",
        violations.length
      ),
      points: pointRowsFromVectors({
        required,
        available,
        reserved,
        spare,
        violations
      }),
      detailLines: humaniseWorkspaceLines([
        `${controllers.length} controller allocation${controllers.length === 1 ? "" : "s"}`,
        `${controllers.reduce((sum, controller) => sum + controller.zoneSlots.length, 0)} zone slot assignment${controllers.length === 1 ? "" : "s"}`,
        `${first.physicalLocation.replaceAll("_", " ")} mounting`
      ]),
      sourceRefCount: controllers.reduce(
        (sum, controller) => sum + controller.sourceRefs.length,
        0
      ),
      sourceRefs,
      ruleTraceRefs: [
        ...new Set(controllers.flatMap((controller) => controller.ruleTraceRefs))
      ],
      sourceItemIds: controllers.map((controller) => controller.id),
      instanceCount: controllers.length,
      isGrouped: controllers.length > 1,
      subItems: [
        {
          id: `workspace-controller-${first.productPartNo}-${first.role}-detail`,
          label: controllerRoleLabel(first.role),
          role: controllerRoleLabel(first.role),
          quantity: controllers.length,
          unit: controllers.length === 1 ? "unit" : "units",
          why: humaniseWorkspaceText(first.why),
          how: humaniseWorkspaceText(first.how),
          detailLines: humaniseWorkspaceLines([
            `${controllers.length} controller allocation${controllers.length === 1 ? "" : "s"}`,
            `${controllers.reduce((sum, controller) => sum + controller.zoneSlots.length, 0)} zone slot assignment${controllers.length === 1 ? "" : "s"}`,
            `${first.physicalLocation.replaceAll("_", " ")} mounting`
          ]),
          sourceItemIds: controllers.map((controller) => controller.id),
          sourceRefCount: controllers.reduce(
            (sum, controller) => sum + controller.sourceRefs.length,
            0
          ),
          ruleTraceRefCount: [
            ...new Set(controllers.flatMap((controller) => controller.ruleTraceRefs))
          ].length
        }
      ]
    };

    return item;
  });
};

export const controllerInstancesFromSolution = (
  solution: ApartmentSolutionForDisplay
): WorkspaceItem[] =>
  solution.controllers.map((controller) => {
    const violations = controller.pointLedger.violations.map(
      (violation) => violation.message
    );
    const role = controllerRoleLabel(controller.role);
    const detailLines = [
      "1 controller allocation",
      `${controller.zoneSlots.length} zone slot assignment${
        controller.zoneSlots.length === 1 ? "" : "s"
      }`,
      `${controller.physicalLocation.replaceAll("_", " ")} mounting`
    ];
    const item: WorkspaceItem = {
      id: `workspace-controller-${controller.id}`,
      category: "Controllers",
      sectionId: "controllers",
      name: controller.productPartNo,
      ...(cleanPartNo(controller.productPartNo)
        ? { partNo: controller.productPartNo }
        : {}),
      role,
      physicalLocation: controller.physicalLocation,
      targetCollection: "controllers",
      selectorRole: controller.role,
      quantity: 1,
      unit: "unit",
      why: humaniseWorkspaceText(controller.why),
      how: humaniseWorkspaceText(controller.how),
      source: "Titan",
      supplySource: "titan_supplied",
      riskBadge: riskBadge(controller.risk),
      validationBadge: validationBadge(
        controller.risk,
        controller.status,
        violations.length
      ),
      points: pointRowsFromVectors({
        required: controller.pointLedger.required,
        available: controller.pointLedger.available,
        reserved: controller.pointLedger.reserved,
        spare: controller.pointLedger.spare,
        violations
      }),
      detailLines: humaniseWorkspaceLines(detailLines),
      sourceRefCount: controller.sourceRefs.length,
      sourceRefs: controller.sourceRefs,
      ruleTraceRefs: controller.ruleTraceRefs,
      sourceItemIds: [controller.id],
      instanceCount: 1,
      isGrouped: false,
      overCapacity: controllerIsOverCapacity(controller.pointLedger),
      subItems: [
        {
          id: `workspace-controller-${controller.id}-detail`,
          label: role,
          role,
          quantity: 1,
          unit: "unit",
          why: humaniseWorkspaceText(controller.why),
          how: humaniseWorkspaceText(controller.how),
          detailLines: humaniseWorkspaceLines(detailLines),
          sourceItemIds: [controller.id],
          sourceRefCount: controller.sourceRefs.length,
          ruleTraceRefCount: controller.ruleTraceRefs.length
        }
      ]
    };

    return item;
  });

const infrastructureLabel = (item: InfrastructureItem): string =>
  item.productPartNo ??
  item.placeholderName ??
  item.capabilityRole ??
  labelise(item.itemKind);

const infrastructureItems = (solution: ApartmentSolutionForDisplay): WorkspaceItem[] =>
  solution.infrastructure.map((item) => {
    const role = productRoleLabel(item.capabilityRole ?? item.itemKind);
    const workspaceItem: WorkspaceItem = {
      id: `workspace-infrastructure-${item.id}`,
      category: "Products",
      sectionId: "other",
      name: infrastructureLabel(item),
      ...(cleanPartNo(item.productPartNo) ? { partNo: item.productPartNo } : {}),
      role,
      targetCollection: "infrastructure",
      ...(item.provisionScope ? { provisionScope: item.provisionScope } : {}),
      ...(item.provisionScopeBasis
        ? { provisionScopeBasis: item.provisionScopeBasis }
        : {}),
      selectorItemKind: item.itemKind,
      quantity: item.quantity,
      unit: item.quantity === 1 ? "item" : "items",
      why: humaniseWorkspaceText(item.why),
      how: humaniseWorkspaceText(item.how),
      source: displaySupplySource(item.supplySource),
      supplySource: item.supplySource,
      riskBadge: riskBadge(item.risk),
      validationBadge: validationBadge(item.risk, item.selectionStatus),
      points: [],
      detailLines: humaniseWorkspaceLines([
        item.installIncluded ? "Install included" : "Install not included",
        item.connectivity ?? "No connectivity emitted for this item",
        ...(item.protocols ?? [])
      ]),
      sourceRefCount: item.sourceRefs.length,
      sourceRefs: item.sourceRefs,
      ruleTraceRefs: item.ruleTraceRefs,
      sourceItemIds: [item.id],
      instanceCount: 1,
      isGrouped: item.quantity > 1,
      subItems: []
    };

    return {
      ...workspaceItem,
      sectionId: sectionForItem(workspaceItem),
      subItems: [rawSubItem(workspaceItem)]
    };
  });

const deviceItems = (solution: ApartmentSolutionForDisplay): WorkspaceItem[] =>
  solution.devices.map((device) => {
    const workspaceItem: WorkspaceItem = {
      id: `workspace-device-${device.id}`,
      category: "Products",
      sectionId: "other",
      name: device.productPartNo,
      partNo: device.productPartNo,
      role: productRoleLabel(device.role),
      targetCollection: "devices",
      ...(device.provisionScope ? { provisionScope: device.provisionScope } : {}),
      ...(device.provisionScopeBasis
        ? { provisionScopeBasis: device.provisionScopeBasis }
        : {}),
      selectorRole: device.role,
      quantity: device.quantity,
      unit: device.quantity === 1 ? "item" : "items",
      why: humaniseWorkspaceText(device.why),
      how: humaniseWorkspaceText(device.how),
      source: "Titan",
      supplySource: "titan_supplied",
      riskBadge: riskBadge(device.risk),
      validationBadge: validationBadge(device.risk),
      points: [],
      detailLines: humaniseWorkspaceLines([
        device.family,
        device.ownerRef ? `Owner ${device.ownerRef}` : "No owner reference emitted",
        `${device.pointDemandRefs.length} point demand reference${device.pointDemandRefs.length === 1 ? "" : "s"}`
      ]),
      sourceRefCount: device.sourceRefs.length,
      sourceRefs: device.sourceRefs,
      ruleTraceRefs: device.ruleTraceRefs,
      sourceItemIds: [device.id],
      instanceCount: 1,
      isGrouped: device.quantity > 1,
      subItems: []
    };

    return {
      ...workspaceItem,
      sectionId: sectionForItem(workspaceItem),
      subItems: [rawSubItem(workspaceItem)]
    };
  });

const edgeItems = (solution: ApartmentSolutionForDisplay): WorkspaceItem[] =>
  (solution.edgeFeatures ?? []).map((feature) => {
    const workspaceItem: WorkspaceItem = {
      id: `workspace-edge-${feature.id}`,
      category: "Extensions",
      sectionId: "edge-leak",
      name: labelise(feature.featureKind),
      role: "Edge extension",
      quantity: 1,
      unit: "feature",
      why: humaniseWorkspaceText(feature.why),
      how: humaniseWorkspaceText(feature.how),
      source: "In-house",
      supplySource: "in_house",
      riskBadge: riskBadge(feature.risk),
      validationBadge: validationBadge(
        feature.needsUserDecision
          ? {
              level: "warning",
              riskRefs: feature.risk.riskRefs,
              notes: "Edge feature needs a user decision."
            }
          : feature.risk
      ),
      points: [],
      detailLines: humaniseWorkspaceLines([
        `Provision ${feature.provisionUnit.replaceAll("_", " ")}`,
        `Input policy ${feature.inputPolicy.replaceAll("_", " ")}`,
        feature.connectivity ?? "No connectivity emitted",
        ...feature.installNotes
      ]),
      sourceRefCount: feature.sourceRefs.length,
      sourceRefs: feature.sourceRefs,
      ruleTraceRefs: feature.ruleTraceRefs,
      sourceItemIds: [feature.id],
      instanceCount: 1,
      isGrouped: false,
      subItems: []
    };

    return {
      ...workspaceItem,
      subItems: [rawSubItem(workspaceItem)]
    };
  });

const solutionHasEdgeOutput = solutionHasExplicitEdgeOutput;

const removedDefaultEdgePartNos = (
  solution: ApartmentSolutionForDisplay
): ReadonlySet<string> =>
  new Set(
    solution.justifications.flatMap((justification) => {
      const removedPartNo = defaultEdgeRemovalPartNoFromTargetRef(
        justification.targetRef
      );

      return removedPartNo ? [removedPartNo] : [];
    })
  );

const defaultEdgeItems = (solution: ApartmentSolutionForDisplay): WorkspaceItem[] =>
  DEFAULT_EDGE_PRODUCTS.filter(
    (product) => !removedDefaultEdgePartNos(solution).has(product.productPartNo)
  ).map((product) => {
    const display = DEFAULT_EDGE_PRODUCT_DISPLAY[product.productPartNo];
    const sourceId = `default-edge-${solution.apartmentTypeId}-${product.itemKind}`;
    const base: WorkspaceItem = {
      id: `workspace-${sourceId}`,
      category: display.category,
      sectionId: display.sectionId,
      name: product.productPartNo,
      partNo: product.productPartNo,
      role: product.role,
      targetCollection: "infrastructure",
      selectorItemKind: product.itemKind,
      quantity: 1,
      unit: display.category === "Extensions" ? "feature" : "item",
      why: DEFAULT_EDGE_WHY,
      how: DEFAULT_EDGE_WHERE,
      source: displaySupplySource(product.supplySource),
      supplySource: product.supplySource,
      riskBadge: riskBadge({
        level: "info",
        riskRefs: [],
        notes: DEFAULT_EDGE_WHY
      }),
      validationBadge: { level: "info", label: "Default" },
      points: [],
      detailLines: humaniseWorkspaceLines(display.detailLines),
      sourceRefCount: 0,
      sourceRefs: [],
      ruleTraceRefs: [],
      sourceItemIds: [sourceId],
      instanceCount: 1,
      isGrouped: false,
      subItems: []
    };

    return {
      ...base,
      subItems: [rawSubItem(base)]
    };
  });

const groupedRationale = (
  items: readonly WorkspaceItem[],
  field: "why" | "how"
): string => {
  const values = uniqueStrings(items.map((item) => item[field]));

  if (values.length <= 1) {
    return values[0] ?? "";
  }

  const subItems = mergeSubItems(items);

  return subItems.map((item) => `${item.role}: ${item[field]}`).join(" ");
};

const aggregateWorkspaceItemGroup = (
  key: string,
  items: readonly WorkspaceItem[]
): WorkspaceItem => {
  const first = items[0];

  if (!first) {
    throw new Error(`Workspace item group ${key} has no items.`);
  }

  const role = aggregateRoleForItem(first);
  const quantity = items.reduce((sum, item) => sum + item.quantity, 0);
  const sourceItemIds = items.flatMap((item) => item.sourceItemIds);
  const subItems = mergeSubItems(items);
  const sourceRefs = uniqueSourceRefs(items.flatMap((item) => item.sourceRefs));
  const ruleTraceRefs = [...new Set(items.flatMap((item) => item.ruleTraceRefs))];
  const targetCollection = commonValue(items.map((item) => item.targetCollection));
  const provisionScope = commonValue(items.map((item) => item.provisionScope));
  const provisionScopeBasis = commonValue(
    items.map((item) => item.provisionScopeBasis)
  );
  const selectorRole = commonValue(items.map((item) => item.selectorRole));
  const selectorItemKind = commonValue(items.map((item) => item.selectorItemKind));
  const detailLines = [
    `${pluralise(sourceItemIds.length, "source row")} grouped into this line`,
    `${pluralise(quantity, first.category === "Controllers" ? "controller" : "physical item")} in the generated output`,
    ...uniqueStrings(items.flatMap((item) => item.detailLines)).slice(0, 8)
  ];
  const groupIdBase = [
    targetCollection ?? first.category,
    first.partNo ?? first.name,
    role.key,
    first.source,
    first.supplySource ?? ""
  ].join("-");

  const item: WorkspaceItem = {
    id: `workspace-${targetCollection ?? "item"}-group-${safeId(groupIdBase)}`,
    category: first.category,
    sectionId: first.sectionId,
    name: first.name,
    ...(cleanPartNo(first.partNo) ? { partNo: first.partNo } : {}),
    role: role.label,
    ...(targetCollection ? { targetCollection } : {}),
    ...(provisionScope ? { provisionScope } : {}),
    ...(provisionScopeBasis ? { provisionScopeBasis } : {}),
    ...(selectorRole ? { selectorRole } : {}),
    ...(selectorItemKind ? { selectorItemKind } : {}),
    quantity,
    unit: unitForQuantity(quantity, first.category),
    why: groupedRationale(items, "why"),
    how: groupedRationale(items, "how"),
    source: first.source,
    ...(first.supplySource ? { supplySource: first.supplySource } : {}),
    riskBadge: maxDisplayRiskBadge(items.map((candidate) => candidate.riskBadge)),
    validationBadge: maxDisplayValidationBadge(
      items.map((candidate) => candidate.validationBadge)
    ),
    points: mergePointUses(items),
    detailLines,
    sourceRefCount: items.reduce((sum, item) => sum + item.sourceRefCount, 0),
    sourceRefs,
    ruleTraceRefs,
    sourceItemIds,
    instanceCount: sourceItemIds.length,
    isGrouped: items.length > 1 || sourceItemIds.length > 1 || quantity > 1,
    subItems
  };

  return {
    ...item,
    sectionId: sectionForItem(item)
  };
};

const aggregateWorkspaceItems = (items: readonly WorkspaceItem[]): WorkspaceItem[] => {
  const groups = new Map<string, WorkspaceItem[]>();

  for (const item of items) {
    const key = workspaceItemGroupKey(item);
    groups.set(key, [...(groups.get(key) ?? []), item]);
  }

  return [...groups.entries()]
    .map(([key, groupItems]) => aggregateWorkspaceItemGroup(key, groupItems))
    .sort((left, right) => {
      const leftSection = workspaceSectionOrder.indexOf(left.sectionId);
      const rightSection = workspaceSectionOrder.indexOf(right.sectionId);

      return (
        leftSection - rightSection ||
        left.name.localeCompare(right.name) ||
        left.role.localeCompare(right.role)
      );
    });
};

export const workspaceItemsFromSolution = (
  solution: ApartmentSolutionForDisplay
): WorkspaceItem[] =>
  aggregateWorkspaceItems([
    ...controllerItems(solution),
    ...deviceItems(solution),
    ...infrastructureItems(solution),
    ...edgeItems(solution),
    ...(shouldInjectDefaultEdgeRows(solution) ? defaultEdgeItems(solution) : [])
  ]);

const countZones = (
  solution: ApartmentSolutionForDisplay,
  predicate: (zoneKind: string) => boolean
): number => solution.logicalZones.filter((zone) => predicate(zone.zoneKind)).length;

const countLoads = (
  solution: ApartmentSolutionForDisplay,
  predicate: (loadKind: string) => boolean
): number =>
  solution.thermalLoads.reduce(
    (sum, load) => (predicate(load.loadKind) ? sum + load.quantity : sum),
    0
  );

const pointDemandsForTakeoffs = (
  solution: ApartmentSolutionForDisplay
): readonly PointDemandForTakeoff[] =>
  (
    solution as ApartmentSolutionForDisplay & {
      pointDemands?: readonly PointDemandForTakeoff[];
    }
  ).pointDemands ?? [];

const countInfrastructure = (
  solution: ApartmentSolutionForDisplay,
  predicate: (itemKind: string, item: InfrastructureItem) => boolean
): number =>
  solution.infrastructure.reduce(
    (sum, item) => (predicate(item.itemKind, item) ? sum + item.quantity : sum),
    0
  );

const countFcuZones = (solution: ApartmentSolutionForDisplay): number => {
  const logical = countZones(solution, (zoneKind) => zoneKind === "fcu");
  return logical > 0 ? logical : countLoads(solution, (loadKind) => loadKind === "fcu");
};

const countUfhZones = (solution: ApartmentSolutionForDisplay): number => {
  const logical = countZones(solution, (zoneKind) => zoneKind.includes("ufh"));
  return logical > 0
    ? logical
    : countLoads(solution, (loadKind) => loadKind.includes("ufh"));
};

const countTowelRails = (solution: ApartmentSolutionForDisplay): number => {
  const loads = countLoads(solution, (loadKind) => loadKind.includes("towel"));
  if (loads > 0) {
    return loads;
  }

  return pointDemandsForTakeoffs(solution).reduce((sum, demand) => {
    if (!demand.demandKind?.includes("towel_rail")) {
      return sum;
    }

    const pointCount = (demand.points?.do ?? 0) + (demand.points?.relay ?? 0);
    return sum + (demand.quantity ?? (pointCount > 0 ? pointCount : 1));
  }, 0);
};

const wetRoomsForHabitableRooms = (habitableRooms: number): number => {
  const bedrooms = Math.max(0, habitableRooms - 2);
  if (bedrooms >= 5) {
    return 3;
  }
  if (bedrooms >= 3) {
    return 2;
  }
  return 1;
};

const countLeakZones = (solution: ApartmentSolutionForDisplay): number => {
  const hasLeakDetection =
    (solution.edgeFeatures ?? []).some(
      (feature) => feature.featureKind === "leak_detection"
    ) ||
    solution.infrastructure.some((item) =>
      `${item.itemKind} ${item.capabilityRole ?? ""} ${item.placeholderName ?? ""}`
        .toLowerCase()
        .includes("leak")
    );

  if (!hasLeakDetection) {
    return 0;
  }

  return wetRoomsForHabitableRooms(countFcuZones(solution)) + 1;
};

const countUtilityMeters = (solution: ApartmentSolutionForDisplay): number =>
  countInfrastructure(solution, (itemKind) => itemKind.includes("metering_unit"));

const takeoffsFromSolution = (
  solution: ApartmentSolutionForDisplay
): WorkspaceTakeoffStat[] => {
  const fanCoils = countFcuZones(solution);
  const stats: WorkspaceTakeoffStat[] = [
    {
      key: "rooms",
      label: "Habitable rooms",
      value: fanCoils,
      tone: "neutral"
    },
    {
      key: "fcu",
      label: "Fan coils",
      value: fanCoils,
      tone: "controller"
    },
    {
      key: "ufh",
      label: "UFH zones",
      value: countUfhZones(solution),
      tone: "heating"
    },
    {
      key: "towel",
      label: "Towel rails",
      value: countTowelRails(solution),
      tone: "heating"
    },
    {
      key: "leak",
      label: "Leak zones",
      value: countLeakZones(solution),
      tone: "network"
    },
    {
      key: "meters",
      label: "Utility meters",
      value: countUtilityMeters(solution),
      tone: "network"
    }
  ];

  return stats;
};

const workspaceSectionsFromItems = (
  items: readonly WorkspaceItem[]
): WorkspaceSection[] =>
  workspaceSectionOrder
    .map((id) => {
      const sectionItems = items.filter((item) => item.sectionId === id);
      const definition = workspaceSectionDefinitions[id];

      return {
        id,
        title: definition.title,
        description: definition.description,
        itemIds: sectionItems.map((item) => item.id),
        totalQuantity: sectionItems.reduce((sum, item) => sum + item.quantity, 0)
      };
    })
    .filter((section) => section.itemIds.length > 0);

const itemQuantity = (
  items: readonly WorkspaceItem[],
  predicate: (item: WorkspaceItem) => boolean
): number =>
  items.reduce((sum, item) => (predicate(item) ? sum + item.quantity : sum), 0);

const roleQuantity = (
  item: WorkspaceItem,
  predicate: (role: string) => boolean
): number =>
  item.subItems.reduce(
    (sum, subItem) => (predicate(subItem.role) ? sum + subItem.quantity : sum),
    0
  );

const summaryDetail = (parts: readonly string[]): string | undefined => {
  const values = parts.filter((part) => part.length > 0);

  return values.length > 0 ? values.join(" + ") : undefined;
};

const summaryFact = (input: {
  id: string;
  label: string;
  value: number;
  detail?: string | undefined;
  tone: WorkspaceTakeoffStat["tone"];
}): WorkspaceSummaryFact => ({
  id: input.id,
  label: input.label,
  value: input.value,
  ...(input.detail ? { detail: input.detail } : {}),
  tone: input.tone
});

const summaryFromItems = (
  solution: ApartmentSolutionForDisplay,
  items: readonly WorkspaceItem[],
  title: string
): WorkspaceSummary => {
  const fanCoilControllers = itemQuantity(
    items,
    (item) =>
      item.category === "Controllers" && item.role.toLowerCase().includes("fan-coil")
  );
  const screenItems = items.filter((item) => item.sectionId === "room-ui");
  const localScreens = screenItems.reduce(
    (sum, item) =>
      sum + roleQuantity(item, (role) => role.toLowerCase().includes("local")),
    0
  );
  const centralScreens = screenItems.reduce(
    (sum, item) =>
      sum + roleQuantity(item, (role) => role.toLowerCase().includes("central")),
    0
  );
  const screens = screenItems.reduce((sum, item) => sum + item.quantity, 0);
  const controlSensors = itemQuantity(
    items,
    (item) =>
      item.partNo === "TPFMTS" ||
      item.role.toLowerCase().includes("control temperature")
  );
  const ufhLimitSensorItems = items.filter(
    (item) =>
      item.partNo === "TPTVAV/L" || item.role.toLowerCase().includes("ufh limit")
  );
  const ufhLimitSensors = ufhLimitSensorItems.reduce(
    (sum, item) => sum + item.quantity,
    0
  );
  const activeUfhLimitSensors = ufhLimitSensorItems.reduce(
    (sum, item) =>
      sum + roleQuantity(item, (role) => role.toLowerCase().includes("active")),
    0
  );
  const spareUfhLimitSensors = ufhLimitSensorItems.reduce(
    (sum, item) =>
      sum + roleQuantity(item, (role) => role.toLowerCase().includes("spare")),
    0
  );
  const netmasters = itemQuantity(
    items,
    (item) => item.partNo?.startsWith("TP-NM") === true && /netmaster/i.test(item.role)
  );
  const relays = itemQuantity(
    items,
    (item) => item.role.toLowerCase().includes("relay") && item.partNo !== "IO/COM-8R"
  );
  const relayModules = itemQuantity(items, (item) =>
    item.role.toLowerCase().includes("relay output")
  );
  const panels = itemQuantity(
    items,
    (item) =>
      item.sectionId === "relays-panel" &&
      /panel|enclosure/i.test(item.role) &&
      !/edge/i.test(item.role)
  );
  const edgeGateways = itemQuantity(
    items,
    (item) =>
      item.partNo === "AUTO-EDGE-GATEWAY" ||
      (item.sectionId === "edge-leak" && item.role.toLowerCase() === "edge gateway")
  );
  const edgeWorkflows =
    itemQuantity(
      items,
      (item) =>
        item.sectionId === "edge-leak" &&
        item.partNo !== "AUTO-EDGE-GATEWAY" &&
        item.role.toLowerCase() !== "edge gateway"
    ) + itemQuantity(items, (item) => item.sectionId === "services");
  const facts: WorkspaceSummaryFact[] = [
    ...(fanCoilControllers > 0
      ? [
          summaryFact({
            id: "fan-coil-controllers",
            label: "Fan-coil controllers",
            value: fanCoilControllers,
            tone: "controller"
          })
        ]
      : []),
    ...(screens > 0
      ? [
          summaryFact({
            id: "control-screens",
            label: "Control screens",
            value: screens,
            detail: summaryDetail([
              localScreens > 0 ? `${localScreens} local` : "",
              centralScreens > 0 ? `${centralScreens} central` : ""
            ]),
            tone: "network"
          })
        ]
      : []),
    ...(controlSensors > 0
      ? [
          summaryFact({
            id: "control-sensors",
            label: "Control sensors",
            value: controlSensors,
            tone: "heating"
          })
        ]
      : []),
    ...(ufhLimitSensors > 0
      ? [
          summaryFact({
            id: "ufh-limit-sensors",
            label: "UFH limit sensors",
            value: ufhLimitSensors,
            detail: summaryDetail([
              activeUfhLimitSensors > 0 ? `${activeUfhLimitSensors} active` : "",
              spareUfhLimitSensors > 0 ? `${spareUfhLimitSensors} spare` : ""
            ]),
            tone: "heating"
          })
        ]
      : []),
    ...(netmasters > 0
      ? [
          summaryFact({
            id: "netmasters",
            label: "Netmaster",
            value: netmasters,
            tone: "network"
          })
        ]
      : []),
    ...(relays + relayModules + panels > 0
      ? [
          summaryFact({
            id: "relays-panel",
            label: "Relays & panel",
            value: relays + relayModules + panels,
            detail: summaryDetail([
              relays > 0 ? pluralise(relays, "relay") : "",
              relayModules > 0 ? pluralise(relayModules, "relay module") : "",
              panels > 0 ? pluralise(panels, "panel") : ""
            ]),
            tone: "loads"
          })
        ]
      : []),
    ...(edgeGateways + edgeWorkflows > 0
      ? [
          summaryFact({
            id: "edge-services",
            label: "Edge gateway",
            value: edgeGateways + edgeWorkflows,
            detail: summaryDetail([
              edgeGateways > 0 ? pluralise(edgeGateways, "gateway") : "",
              edgeWorkflows > 0 ? pluralise(edgeWorkflows, "workflow") : ""
            ]),
            tone: "network"
          })
        ]
      : [])
  ];
  const sentenceParts = [
    fanCoilControllers > 0 ? pluralise(fanCoilControllers, "fan-coil controller") : "",
    screens > 0
      ? `${pluralise(screens, "control screen")}${
          localScreens + centralScreens > 0
            ? ` (${summaryDetail([
                localScreens > 0 ? `${localScreens} local` : "",
                centralScreens > 0 ? `${centralScreens} central` : ""
              ])})`
            : ""
        }`
      : "",
    controlSensors > 0 ? pluralise(controlSensors, "control sensor") : "",
    ufhLimitSensors > 0 ? pluralise(ufhLimitSensors, "UFH limit sensor") : "",
    netmasters > 0 ? pluralise(netmasters, "Netmaster") : "",
    edgeGateways + edgeWorkflows > 0 ? "Edge gateway and leak-detection workflow" : "",
    relays + relayModules + panels > 0 ? "relays and control panel hardware" : ""
  ].filter((part) => part.length > 0);
  const physicalItemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const sourceRowCount = items.reduce((sum, item) => sum + item.instanceCount, 0);
  const sentenceBody =
    sentenceParts.length > 0
      ? sentenceParts.join(", ")
      : `${pluralise(physicalItemCount, "generated item")} from the engine output`;

  return {
    sentence: `${title} (x${solution.quantity} in this project) is specified with: ${sentenceBody}.`,
    facts,
    groupedLineCount: items.length,
    physicalItemCount,
    sourceRowCount
  };
};

const notesFromSolution = (
  solution: ApartmentSolutionForDisplay,
  items: readonly WorkspaceItem[]
): WorkspaceNote[] => {
  const riskNotes: WorkspaceNote[] = solution.risks.slice(0, 3).map((risk) => ({
    id: `note-${risk.id}`,
    kind: risk.type === "needsUserDecision" ? "clarification" : "risk",
    title: risk.title,
    body: risk.plainEnglish,
    noPriceImpact: !risk.blocksExport,
    linkedItemIds: items
      .filter((item) => item.riskBadge.riskRefs.includes(risk.id))
      .map((item) => item.id)
  }));

  const assumptionNotes: WorkspaceNote[] = solution.justifications.flatMap(
    (justification) =>
      justification.assumptions.slice(0, 2).map((assumption, index) => ({
        id: `note-${justification.id}-${index}`,
        kind: "assumption" as const,
        title: "Engine assumption",
        body: assumption,
        noPriceImpact: true,
        linkedItemIds: []
      }))
  );

  return [...riskNotes, ...assumptionNotes];
};

const dataGapsFromSolution = (solution: ApartmentSolutionForDisplay): string[] => {
  const gaps: string[] = [];
  const hasNetmaster = solution.infrastructure.some(
    (item) => item.itemKind === "netmaster"
  );
  const hasMeters = solution.infrastructure.some(
    (item) => item.itemKind === "metering_unit"
  );
  const hasEdge = solutionHasEdgeOutput(solution);
  const hasDevices = solution.devices.length > 0;

  if (!hasNetmaster) {
    gaps.push("No generated Netmaster/hub item in this fixture.");
  }

  if (!hasMeters) {
    gaps.push("No generated meter hardware in this fixture.");
  }

  if (!hasEdge) {
    gaps.push(
      `${DEFAULT_NO_TRIGGER_LABEL}. Edge is included in the workspace without a source trail because no project-specific Edge requirement was found.`
    );
  }

  if (!hasDevices) {
    gaps.push("No generated room-display or sensor device rows in this fixture.");
  }

  return gaps;
};

export const apartmentSolutionToDisplayView = (
  solution: ApartmentSolutionForDisplay,
  options: DisplayViewOptions
): ApartmentSolutionDisplayViewModel => {
  const items = workspaceItemsFromSolution(solution);
  const meta = typeMetaById(options).get(solution.apartmentTypeId);
  const title = meta?.label ?? labelise(solution.apartmentTypeId);

  return {
    projectId: options.projectId,
    apartmentTypeId: solution.apartmentTypeId,
    specSnapshotId: options.specSnapshotId,
    status: solution.validation.status === "rejected" ? "blocked" : "ready",
    controllerIds: solution.controllers.map((controller) => controller.id),
    deviceIds: [
      ...solution.devices.map((device) => device.id),
      ...solution.infrastructure.map((item) => item.id),
      ...(solution.edgeFeatures ?? []).map((feature) => feature.id)
    ],
    mappingIds: [
      ...solution.physicalMappings.map((mapping) => mapping.id),
      ...solution.mappings.map((mapping) => mapping.id)
    ],
    riskIds: solution.risks.map((risk) => risk.id),
    updatedAt: options.updatedAt,
    title,
    subtitle: `${solution.quantity} home${solution.quantity === 1 ? "" : "s"} of this type in the generated fixture`,
    apartmentQuantity: solution.quantity,
    ...(meta?.beds !== undefined ? { beds: meta.beds } : {}),
    takeoffs: takeoffsFromSolution(solution),
    summary: summaryFromItems(solution, items, title),
    sections: workspaceSectionsFromItems(items),
    items,
    pointLedger: {
      required: solution.pointLedger.required,
      available: solution.pointLedger.available,
      reserved: solution.pointLedger.reserved,
      spare: solution.pointLedger.spare,
      violations: solution.pointLedger.violations.map((violation) => violation.message),
      rows: pointRows(solution.pointLedger)
    },
    notes: notesFromSolution(solution, items),
    dataGaps: dataGapsFromSolution(solution),
    generatedFrom: options.generatedFrom
  };
};

const matrixItemKey = (item: WorkspaceItem): string =>
  [item.partNo ?? item.name, item.role, item.sectionId].join("\u001f");

const matrixItemLabel = (item: WorkspaceItem): string =>
  item.partNo ? `${item.partNo} - ${item.role}` : item.name;

// Cabling (Modbus / RS-485 / M-Bus / metering-bus / daisy-chain, etc.) is
// secondary infrastructure and must never surface as a comparison row — it is
// listed separately. Shares the predicate used by the project-level
// Take-offs matrix so the two views agree on the exclusion.
const isCablingMatrixItem = (item: WorkspaceItem): boolean =>
  isCablingDescriptor(
    [item.selectorItemKind, item.partNo, item.name, item.role].filter(Boolean).join(" ")
  );

const itemMapBySolution = (
  solutions: readonly ApartmentSolutionForDisplay[]
): Map<string, Map<string, WorkspaceItem>> => {
  const outer = new Map<string, Map<string, WorkspaceItem>>();

  for (const solution of solutions) {
    outer.set(
      solution.apartmentTypeId,
      new Map(
        workspaceItemsFromSolution(solution).map((item) => [matrixItemKey(item), item])
      )
    );
  }

  return outer;
};

const matrixGroupId = (category: string): string =>
  `matrix-group-${category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

const metricDefinitions: {
  key: MatrixAnomalyMetric;
  label: string;
  threshold: number;
  value: (solution: ApartmentSolutionForDisplay) => number;
}[] = [
  {
    key: "controllers",
    label: "Controllers",
    threshold: 1,
    value: (solution) => solution.controllers.length
  },
  {
    key: "zones",
    label: "Logical zones",
    threshold: 2,
    value: (solution) => solution.logicalZones.length
  },
  {
    key: "uiScreens",
    label: "UI screens",
    threshold: 1,
    value: (solution) =>
      solution.devices
        .filter((device) =>
          /ui|screen|display/i.test(
            `${device.role} ${device.family} ${device.productPartNo}`
          )
        )
        .reduce((sum, device) => sum + device.quantity, 0)
  },
  {
    key: "ioModules",
    label: "I/O modules",
    threshold: 1,
    value: (solution) =>
      solution.infrastructure
        .filter((item) => item.itemKind === "io_module")
        .reduce((sum, item) => sum + item.quantity, 0)
  }
];

const modeNumber = (values: readonly number[]): number => {
  const counts = new Map<number, number>();

  for (const value of values) {
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }

  return (
    [...counts.entries()].sort(
      ([leftValue, leftCount], [rightValue, rightCount]) =>
        rightCount - leftCount || leftValue - rightValue
    )[0]?.[0] ?? 0
  );
};

const peerGroupIdForBeds = (beds: number | undefined): string =>
  beds === undefined ? "peer-generated" : `peer-${beds}-bed`;

const peerGroupLabelForBeds = (beds: number | undefined): string =>
  beds === undefined ? "Generated types" : `${beds}-bed`;

const typeMetaById = (
  options: DisplayViewOptions
): ReadonlyMap<string, ApartmentTypeDisplayMeta> =>
  new Map((options.apartmentTypes ?? []).map((meta) => [meta.apartmentTypeId, meta]));

const matrixColumnGroups = (
  solutions: readonly ApartmentSolutionForDisplay[],
  metaById: ReadonlyMap<string, ApartmentTypeDisplayMeta>,
  anomaliesByTypeId: ReadonlyMap<string, readonly SpecMatrixColumnAnomaly[]>
): SpecMatrixColumnGroup[] => {
  const groups = new Map<
    string,
    {
      label: string;
      columnKeys: string[];
      anomalyCount: number;
    }
  >();

  for (const solution of solutions) {
    const meta = metaById.get(solution.apartmentTypeId);
    const groupId = peerGroupIdForBeds(meta?.beds);
    const group = groups.get(groupId) ?? {
      label: peerGroupLabelForBeds(meta?.beds),
      columnKeys: [],
      anomalyCount: 0
    };

    group.columnKeys.push(solution.apartmentTypeId);
    group.anomalyCount += anomaliesByTypeId.get(solution.apartmentTypeId)?.length ?? 0;
    groups.set(groupId, group);
  }

  return [...groups.entries()].map(([id, group]) => ({
    id,
    label: group.label,
    description: `${group.columnKeys.length} type${group.columnKeys.length === 1 ? "" : "s"} · modal controller, zone, UI and I/O baselines`,
    columnKeys: group.columnKeys,
    anomalyCount: group.anomalyCount
  }));
};

const matrixAnomalies = (
  solutions: readonly ApartmentSolutionForDisplay[],
  metaById: ReadonlyMap<string, ApartmentTypeDisplayMeta>
): ReadonlyMap<string, SpecMatrixColumnAnomaly[]> => {
  const grouped = new Map<string, ApartmentSolutionForDisplay[]>();

  for (const solution of solutions) {
    const groupId = peerGroupIdForBeds(metaById.get(solution.apartmentTypeId)?.beds);
    grouped.set(groupId, [...(grouped.get(groupId) ?? []), solution]);
  }

  const anomaliesByType = new Map<string, SpecMatrixColumnAnomaly[]>();

  for (const groupSolutions of grouped.values()) {
    if (groupSolutions.length < 3) {
      continue;
    }

    const first = groupSolutions[0];
    const peerGroupLabel = first
      ? peerGroupLabelForBeds(metaById.get(first.apartmentTypeId)?.beds)
      : "Peer group";

    for (const metric of metricDefinitions) {
      const expected = modeNumber(
        groupSolutions.map((solution) => metric.value(solution))
      );

      for (const solution of groupSolutions) {
        const value = metric.value(solution);
        const delta = value - expected;

        if (Math.abs(delta) < metric.threshold) {
          continue;
        }

        const anomaly: SpecMatrixColumnAnomaly = {
          metric: metric.key,
          label: metric.label,
          value,
          expected,
          delta,
          peerGroupLabel,
          summary: `${metric.label} ${value} vs ${peerGroupLabel} norm ${expected}`
        };

        anomaliesByType.set(solution.apartmentTypeId, [
          ...(anomaliesByType.get(solution.apartmentTypeId) ?? []),
          anomaly
        ]);
      }
    }
  }

  return anomaliesByType;
};

export const solutionsToSpecMatrixDisplayView = (
  solutions: readonly ApartmentSolutionForDisplay[],
  options: DisplayViewOptions
): SpecMatrixDisplayViewModel => {
  const metaById = typeMetaById(options);
  const anomaliesByTypeId = matrixAnomalies(solutions, metaById);
  const bySolution = itemMapBySolution(solutions);
  const itemKeys = [
    ...new Set([...bySolution.values()].flatMap((items) => [...items.keys()]))
  ].sort((left, right) => left.localeCompare(right));
  const columns = solutions.map((solution) => {
    const meta = metaById.get(solution.apartmentTypeId);
    const anomalies = anomaliesByTypeId.get(solution.apartmentTypeId) ?? [];

    return {
      apartmentTypeId: solution.apartmentTypeId,
      label: meta?.label ?? labelise(solution.apartmentTypeId),
      quantity: solution.quantity,
      ...(meta?.beds !== undefined ? { beds: meta.beds } : {}),
      peerGroupId: peerGroupIdForBeds(meta?.beds),
      peerGroupLabel: peerGroupLabelForBeds(meta?.beds),
      anomalyCount: anomalies.length,
      anomalies: [...anomalies]
    };
  });
  const columnGroups = matrixColumnGroups(solutions, metaById, anomaliesByTypeId);
  const metricRows: SpecMatrixRow[] = metricDefinitions.map((metric) => ({
    id: `matrix-metric-${metric.key}`,
    label: metric.label,
    category: "Peer metrics",
    cells: solutions.map((solution) => {
      const anomaly = anomaliesByTypeId
        .get(solution.apartmentTypeId)
        ?.find((candidate) => candidate.metric === metric.key);
      const base: SpecMatrixCell = {
        apartmentTypeId: solution.apartmentTypeId,
        quantity: metric.value(solution),
        riskLevel: anomaly ? "warning" : "none",
        validationLabel: anomaly?.summary ?? "Peer baseline"
      };

      return anomaly
        ? {
            ...base,
            isAnomaly: true,
            anomalyMetric: metric.key,
            anomalyLabel: anomaly.summary
          }
        : base;
    })
  }));
  const itemRows = itemKeys
    .map((itemKey): SpecMatrixRow | null => {
      const representative = solutions
        .map((solution) => bySolution.get(solution.apartmentTypeId)?.get(itemKey))
        .find((item): item is WorkspaceItem => item !== undefined);

      if (!representative) {
        throw new Error(`Matrix row ${itemKey} has no representative item.`);
      }

      // Drop cabling / metering-bus rows: they are separate infrastructure,
      // never a controls/take-off comparison row.
      if (isCablingMatrixItem(representative)) {
        return null;
      }

      return {
        id: `matrix-row-${safeId(itemKey)}`,
        label: matrixItemLabel(representative),
        category: representative.category,
        cells: solutions.map((solution) => {
          const item = bySolution.get(solution.apartmentTypeId)?.get(itemKey);
          const base = {
            apartmentTypeId: solution.apartmentTypeId,
            quantity: item?.quantity ?? 0,
            riskLevel: item?.riskBadge.level ?? ("none" as const),
            validationLabel: item?.validationBadge.label ?? "Not generated"
          };

          return item ? { ...base, source: item.source } : base;
        })
      };
    })
    .filter((row): row is SpecMatrixRow => row !== null);
  const rows = [...metricRows, ...itemRows];
  const groups = [...new Set(rows.map((row) => row.category))].map((category) => ({
    id: matrixGroupId(category),
    label: category,
    rows: rows.filter((row) => row.category === category)
  }));

  return {
    projectId: options.projectId,
    specSnapshotId: options.specSnapshotId,
    status: "ready",
    apartmentTypeIds: solutions.map((solution) => solution.apartmentTypeId),
    columnKeys: columns.map((column) => column.apartmentTypeId),
    updatedAt: options.updatedAt,
    columns,
    columnGroups,
    groups,
    dataGaps:
      solutions.length < 2
        ? [
            "Only one generated apartment-solution fixture is available, so the matrix is ready but not yet a multi-type comparison."
          ]
        : [],
    generatedFrom: options.generatedFrom
  };
};
